// v10/src/components/AuthScreen.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authService, storeTokens } from '../services/apiService';
import HUDBackground from './HUDBackground';
import RegisterAeroclub from './RegisterAeroclub';
import OnboardingSlides from './OnboardingSlides';
import useWebAuthn from '../hooks/useWebAuthn';
import { shouldRedirectToDemoHost, DEMO_HOST_REDIRECT_TARGET } from '../utils/demoRedirect';

// ─── Input field ──────────────────────────────────────────────────
const AuthInput = ({ icon: Icon, error, rightEl, ...props }) => (
  <div style={{ position: 'relative' }}>
    <Icon style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: error ? 'rgba(248,113,113,0.8)' : 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
    <input
      {...props}
      style={{
        width: '100%', padding: '13px 14px 13px 40px',
        background: 'rgba(255,255,255,0.06)',
        border: `1.5px solid ${error ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: '14px', fontSize: '14px', fontWeight: 500,
        color: 'white', outline: 'none', fontFamily: 'inherit',
        boxSizing: 'border-box', transition: 'border-color 0.18s, background 0.18s',
        paddingRight: rightEl ? '42px' : '14px',
      }}
      onFocus={e => { e.target.style.borderColor = 'rgba(139,92,246,0.6)'; e.target.style.background = 'rgba(255,255,255,0.09)'; }}
      onBlur={e => { e.target.style.borderColor = error ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.06)'; }}
    />
    {rightEl && <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>{rightEl}</div>}
    <AnimatePresence>
      {error && (
        <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          style={{ margin: '5px 0 0 4px', fontSize: '12px', color: 'rgba(248,113,113,0.9)', fontWeight: 500 }}>
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

// ── Language Switcher ─────────────────────────────────────────────
const LANGUAGES = [
  { code: 'es-AR', label: 'ES', flag: '🇦🇷' },
  { code: 'en',    label: 'EN', flag: '🇺🇸' },
];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('condor360_language', code);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(p => !p)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          fontSize: '11.5px', fontWeight: 600, color: 'rgba(255,255,255,0.28)',
          display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit',
          lineHeight: 1,
        }}>
        🌐 {current.label} ▾
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.95 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(15,20,40,0.98)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px', padding: '4px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              minWidth: '110px', zIndex: 100,
            }}>
            {LANGUAGES.map(lang => (
              <button key={lang.code} onClick={() => handleSelect(lang.code)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '8px 12px', borderRadius: '8px', border: 'none',
                  background: i18n.language === lang.code ? 'rgba(255,255,255,0.08)' : 'transparent',
                  cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                  color: i18n.language === lang.code ? 'white' : 'rgba(255,255,255,0.45)',
                }}>
                <span>{lang.flag}</span> {lang.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Legal Modal ───────────────────────────────────────────────────
const LegalModal = ({ title, onClose, children }) => {
  const { t } = useTranslation('auth');
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
        onClick={e => e.stopPropagation()}
        style={{ background: '#0f1929', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', maxWidth: '520px', width: '100%', maxHeight: '78vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: 'white' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '10px', width: '32px', height: '32px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', fontSize: '13px', lineHeight: '1.7', color: 'rgba(255,255,255,0.6)' }}>{children}</div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={onClose} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>{t('understood')}</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── T&C — Versión 1.4 ─────────────────────────────────────────────
const TERMS_CONTENT = (
  <div>
    <p style={{marginTop:0}}><strong style={{color:'rgba(255,255,255,0.8)'}}>Versión 1.4 — Mayo 2026</strong></p>
    <p style={{fontSize:'12px',color:'rgba(248,113,113,0.85)',fontWeight:600,padding:'8px 12px',borderRadius:'10px',background:'rgba(248,113,113,0.08)',border:'1px solid rgba(248,113,113,0.2)'}}>
      ⚠️ Condor360 es una herramienta de ASISTENCIA ADMINISTRATIVA y digitalización operativa. No reemplaza los procedimientos oficiales del fabricante, ni la consulta de fuentes oficiales (AIP/SMN/ANAC), ni la responsabilidad final del Piloto al Mando.
    </p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>1. Descripción del servicio</h3>
    <p>Condor360 es una aplicación web progresiva (PWA) que asiste a pilotos, instructores y personal autorizado en la gestión administrativa de operaciones de aeroclubes, incluyendo digitalización de checklists, registro de squawks, horómetros, libro de vuelos, peso y balance, y planificación de navegación.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>2. Registro y acceso</h3>
    <p>El acceso requiere registro previo autorizado por el administrador de la Organización. Cada usuario es responsable de la seguridad de sus credenciales.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>3. Responsabilidad del Piloto al Mando</h3>
    <p>La verificación física de la aeronave, la decisión de despegue, la conducción del vuelo y la aptitud final para volar recaen exclusivamente en el Piloto al Mando. Condor360 no asume responsabilidad alguna por decisiones operacionales basadas en información provista por la plataforma.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>3.bis Naturaleza referencial de los cálculos</h3>
    <p>Los cálculos provistos por Condor360 — incluyendo pero no limitado a peso y balance, planificación de navegación, performance, meteorología y elevaciones de aeródromos — son <strong style={{color:'rgba(255,255,255,0.85)'}}>REFERENCIALES</strong>. El piloto y/o operador debe validar todos los datos contra:</p>
    <ul style={{marginTop:'4px',paddingLeft:'18px'}}>
      <li>(a) el POH oficial del fabricante de la aeronave específica;</li>
      <li>(b) el manifiesto oficial de peso vacío y momento de la matrícula concreta;</li>
      <li>(c) METAR/TAF oficiales del Servicio Meteorológico Nacional (SMN);</li>
      <li>(d) la AIP de la ANAC vigente;</li>
      <li>(e) NOTAMs aplicables a la ruta y aeródromos involucrados.</li>
    </ul>
    <p>Condor360 utiliza fuentes de datos de terceros (incluyendo OpenWeatherMap y OurAirports) y NO garantiza que dichas fuentes coincidan con las fuentes oficiales argentinas en todo momento.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>3.ter Exclusión de safety-of-flight</h3>
    <p>Condor360 <strong style={{color:'rgba(255,255,255,0.85)'}}>NO es un sistema certificado</strong> bajo DO-178C, ARP4761, TSO de FAA, ni normativa equivalente de ANAC, EASA u otra autoridad aeronáutica civil o militar. Su uso en decisiones operacionales críticas (despegue, navegación, aproximación, aterrizaje, manejo de emergencias) queda EXCLUSIVAMENTE bajo responsabilidad del Piloto al Mando y del operador de la aeronave.</p>
    <p>La plataforma es una herramienta de asistencia administrativa y de digitalización operativa. No reemplaza ni los procedimientos oficiales del fabricante, ni la consulta de fuentes regulatorias oficiales, ni el criterio profesional del piloto, instructor o equipo técnico del aeroclub.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>4. Propiedad intelectual</h3>
    <p>El software, marca y contenidos de Condor360 son propiedad de su titular. Los datos cargados por la Organización (checklists, aeronaves, pilotos) son propiedad de la Organización. Condor360 actúa como custodio técnico de dichos datos bajo los términos del servicio contratado.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>5. Privacidad y tratamiento de datos</h3>
    <p>El tratamiento de datos personales se rige por la Política de Privacidad de Condor360, conforme a la Ley 25.326 de Protección de Datos Personales de Argentina. Ver Política de Privacidad para detalles.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>6. Disponibilidad del servicio</h3>
    <p>Condor360 hace esfuerzos razonables para mantener el servicio disponible pero no garantiza disponibilidad 100%. La plataforma puede experimentar ventanas de mantenimiento, fallos de proveedores third-party (Vercel, Render, Neon, OpenWeatherMap, ElevenLabs) o eventos fuera de control.</p>
    <p>El operador debe contar con procedimientos alternativos (papel, otros sistemas) para casos de indisponibilidad de Condor360.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>7. Cancelación</h3>
    <p>La Organización puede cancelar el servicio en cualquier momento sin penalidad. Los datos quedan disponibles para export durante 90 días posteriores a la cancelación.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>8. Modificaciones a estos términos</h3>
    <p>Condor360 puede actualizar estos términos. Cambios materiales serán notificados con al menos 30 días de anticipación y requerirán re-aceptación explícita del usuario.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>9. Jurisdicción</h3>
    <p>Estos términos se rigen por las leyes de la República Argentina. Cualquier disputa será resuelta en los tribunales ordinarios de la Ciudad Autónoma de Buenos Aires.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>10. Contacto</h3>
    <p>Consultas: <strong style={{color:'rgba(255,255,255,0.7)'}}>info@condor360.app</strong></p>
  </div>
);

// ── Política de Privacidad — Versión 1.4 ──────────────────────────
const PRIVACY_CONTENT = (
  <div>
    <p style={{marginTop:0}}><strong style={{color:'rgba(255,255,255,0.8)'}}>Versión 1.4 — Mayo 2026</strong></p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>1. Responsable del tratamiento</h3>
    <p>La Organización (aeroclub o escuela de vuelo) que contrata Condor360 es el responsable del tratamiento de los datos personales de sus usuarios. Condor360 actúa como encargado del tratamiento (custodio técnico) de dichos datos.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>2. Datos recolectados</h3>
    <p>Recolectamos los siguientes datos:</p>
    <ul style={{marginTop:'4px',paddingLeft:'18px'}}>
      <li>Datos de identificación: nombre, email, DNI (opcional).</li>
      <li>Datos profesionales: licencia de piloto, vencimiento CMA, horas voladas.</li>
      <li>Datos de uso: checklists ejecutados, vuelos registrados, squawks reportados.</li>
      <li>Datos técnicos: IP, navegador, dispositivo, logs de acceso.</li>
    </ul>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>3. Finalidad del tratamiento</h3>
    <p>Los datos se utilizan para:</p>
    <ul style={{marginTop:'4px',paddingLeft:'18px'}}>
      <li>Prestar el servicio contratado por la Organización.</li>
      <li>Mantener registro y audit trail de operaciones del aeroclub.</li>
      <li>Cumplir obligaciones legales aplicables.</li>
      <li>Mejorar el servicio (analytics agregado, no individual).</li>
    </ul>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>4. Conservación</h3>
    <p>Los datos se conservan mientras dure la relación contractual con la Organización, más 90 días posteriores para permitir export. Pasado ese período, los datos personales son anonimizados o eliminados, salvo obligación legal de conservación.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>5. Transferencias internacionales</h3>
    <p>Los datos se almacenan en servidores Neon Postgres (Estados Unidos). Esta transferencia internacional cumple con los recaudos del artículo 12 de la Ley 25.326.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>6. Terceros</h3>
    <p>Condor360 utiliza los siguientes proveedores third-party que pueden procesar datos en el contexto de la prestación del servicio:</p>
    <ul style={{marginTop:'4px',paddingLeft:'18px'}}>
      <li>Vercel (hosting frontend, EE.UU.)</li>
      <li>Render (hosting backend, EE.UU.)</li>
      <li>Neon (base de datos, EE.UU.)</li>
      <li>Cloudinary (almacenamiento de imágenes, EE.UU.)</li>
      <li>Resend (envío de emails, EE.UU.)</li>
      <li>OpenWeatherMap (datos meteorológicos referenciales)</li>
      <li>ElevenLabs (síntesis de voz para asistente Valentina)</li>
      <li>Anthropic (procesamiento de IA para asistencia)</li>
    </ul>
    <p>Ninguno de estos proveedores recibe datos personales innecesarios para su función.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>7. Derechos del usuario</h3>
    <p>Conforme a la Ley 25.326, todo usuario tiene derecho a:</p>
    <ul style={{marginTop:'4px',paddingLeft:'18px'}}>
      <li>Acceder a sus datos personales.</li>
      <li>Rectificar datos inexactos.</li>
      <li>Solicitar supresión de datos.</li>
      <li>Oponerse al tratamiento en casos previstos por la ley.</li>
    </ul>
    <p>Para ejercer estos derechos: <strong style={{color:'rgba(255,255,255,0.7)'}}>info@condor360.app</strong></p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>8. Seguridad</h3>
    <p>Condor360 implementa medidas técnicas y organizativas razonables para proteger los datos: cifrado en tránsito (HTTPS), control de acceso por roles, JWT con expiración, backups automáticos diarios, segregación por organización (multi-tenancy).</p>
    <p>Sin embargo, ningún sistema es 100% seguro. La Organización debe contribuir a la seguridad: gestionar correctamente las altas/bajas de usuarios, instruir sobre uso de contraseñas seguras, etc.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>9. Autoridad de control</h3>
    <p>La autoridad de control en Argentina es la Agencia de Acceso a la Información Pública (AAIP). Ante incumplimientos, el usuario puede presentar denuncia ante esa autoridad.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>10. Modificaciones</h3>
    <p>Esta política puede ser actualizada. Cambios materiales serán notificados con al menos 30 días de anticipación.</p>

    <h3 style={{color:'#60a5fa',marginTop:'16px',fontSize:'13px'}}>11. Contacto</h3>
    <p><strong style={{color:'rgba(255,255,255,0.7)'}}>info@condor360.app</strong></p>
  </div>
);

// ─── Forgot Password Modal ────────────────────────────────────────
const ForgotPasswordModal = ({ onClose }) => {
  const { t } = useTranslation('auth');
  const [email, setEmail]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [sent, setSent]                       = useState(false);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [error, setError]                     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.isGoogleAccount) { setIsGoogleAccount(true); return; }
      if (data.success) setSent(true);
      else setError(data.message || 'Error al enviar el email');
    } catch { setError('Error de conexión. Verificá el servidor.'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
        onClick={e => e.stopPropagation()}
        style={{ background: '#0f1929', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', maxWidth: '400px', width: '100%', padding: '32px', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🔐</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: 'white' }}>{t('forgot_title')}</h3>
              <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>{t('forgot_subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '10px', width: '32px', height: '32px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {sent ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📬</div>
            <p style={{ margin: '0 0 8px', fontWeight: 800, fontSize: '16px', color: 'white' }}>{t('email_sent_title')}</p>
            <p style={{ margin: '0 0 24px', fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: t('email_sent_desc') }} />
            <button onClick={onClose} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>{t('understood')}</button>
          </motion.div>
        ) : isGoogleAccount ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block' }}>
                <rect width="52" height="52" rx="14" fill="rgba(255,255,255,0.06)" />
                <path d="M39.46 26.56c0-1.04-.09-2.04-.26-3H26v5.68h7.5c-.32 1.73-1.3 3.2-2.77 4.19v3.48h4.48c2.62-2.41 4.25-5.97 4.25-10.35z" fill="#4285F4"/>
                <path d="M26 40c3.76 0 6.91-1.25 9.21-3.39l-4.48-3.48c-1.25.84-2.84 1.34-4.73 1.34-3.64 0-6.72-2.46-7.82-5.77H13.6v3.59A13.997 13.997 0 0026 40z" fill="#34A853"/>
                <path d="M18.18 28.7A8.41 8.41 0 0117.74 26c0-.94.16-1.85.44-2.7v-3.59H13.6A14 14 0 0012 26c0 2.26.54 4.39 1.6 6.29l4.58-3.59z" fill="#FBBC05"/>
                <path d="M26 19.53c2.05 0 3.89.71 5.34 2.09l4-4A13.85 13.85 0 0026 12a13.997 13.997 0 00-12.4 7.71l4.58 3.59c1.1-3.31 4.18-5.77 7.82-5.77z" fill="#EA4335"/>
              </svg>
            </div>
            <p style={{ margin: '0 0 8px', fontWeight: 800, fontSize: '16px', color: 'white' }}>{t('google_account_title')}</p>
            <p style={{ margin: '0 0 24px', fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.65 }}>{t('google_account_desc')}</p>
            <button onClick={onClose} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>{t('understood')}</button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{t('forgot_desc')}</p>
            <div style={{ position: 'relative' }}>
              <Mail style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
              <input type="email" required value={email} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder="tu@email.com" 
                style={{ width: '100%', padding: '13px 14px 13px 40px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.1)', borderRadius: '14px', fontSize: '14px', color: 'white', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            {error && <p style={{ margin: 0, fontSize: '12px', color: 'rgba(248,113,113,0.9)', fontWeight: 500 }}>{error}</p>}
            <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
              <motion.button type="submit" disabled={loading} whileHover={!loading ? { scale: 1.02 } : {}} whileTap={!loading ? { scale: 0.97 } : {}}
                style={{ flex: 1, padding: '13px', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: 700, fontSize: '14px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'inherit' }}>
                {loading
                  ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '15px', height: '15px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} />{t('sending')}</>
                  : t('send_instructions')
                }
              </motion.button>
              <button type="button" onClick={onClose} style={{ padding: '13px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>{t('cancel')}</button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};

// ─── Reset Password Screen ────────────────────────────────────────
const ResetPasswordScreen = ({ token, onDone }) => {
  const { t } = useTranslation('auth');
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [showPwd, setShowPwd]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess]       = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    fetch(`${apiUrl}/auth/reset-password/verify?token=${token}`)
      .then(r => r.json())
      .then(d => { setTokenValid(d.valid); setValidating(false); })
      .catch(() => { setTokenValid(false); setValidating(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError(t('passwords_no_match')); return; }
    setError(''); setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (data.success) { setSuccess(true); setTimeout(onDone, 2500); }
      else setError(data.message || t('error_reset_password',{ns:'admin'}));
    } catch { setError(t('error_connection',{ns:'common'})); }
    finally { setLoading(false); }
  };

  const inputStyle = { width: '100%', padding: '13px 14px 13px 40px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.1)', borderRadius: '14px', fontSize: '14px', color: 'white', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', overflow: 'hidden' }}>
      <HUDBackground />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '400px', background: 'rgba(10,15,30,0.75)', backdropFilter: 'blur(28px)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '28px', padding: '36px 32px', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '18px', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="/condor-logo.png" alt="Cóndor" style={{ width:'44px', height:'44px', objectFit:'contain', mixBlendMode:'screen' }} /></div>
          <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 900, background: 'linear-gradient(135deg,#93c5fd,#c4b5fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Condor360</h1>
        </div>
        {validating ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              style={{ width: '32px', height: '32px', borderRadius: '50%', border: '3px solid transparent', borderTopColor: '#7c3aed', margin: '0 auto 12px' }} />
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>{t('verifying_link')}</p>
          </div>
        ) : !tokenValid ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏱</div>
            <p style={{ fontWeight: 800, fontSize: '16px', color: 'white', margin: '0 0 8px' }}>{t('invalid_token_title')}</p>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: '0 0 24px' }}>{t('invalid_token_desc')}</p>
            <button onClick={onDone} style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>{t('back_to_login')}</button>
          </div>
        ) : success ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <p style={{ fontWeight: 800, fontSize: '16px', color: 'white', margin: '0 0 8px' }}>{t('password_updated')}</p>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>{t('redirecting')}</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '14px', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>{t('choose_password')}</p>
            <div style={{ position: 'relative' }}>
              <Lock style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
              <input type={showPwd ? 'text' : 'password'} required value={password} onChange={e => { setPassword(e.target.value); setError(''); }} placeholder={t('new_password')} style={{ ...inputStyle, paddingRight: '42px' }} />
              <button type="button" onClick={() => setShowPwd(p => !p)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center' }}>
                {showPwd ? <EyeOff style={{ width: '16px', height: '16px' }} /> : <Eye style={{ width: '16px', height: '16px' }} />}
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <Lock style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
              <input type="password" required value={confirm} onChange={e => { setConfirm(e.target.value); setError(''); }} placeholder={t('confirm_password')} style={inputStyle} />
            </div>
            {error && <p style={{ margin: 0, fontSize: '12px', color: 'rgba(248,113,113,0.9)', fontWeight: 500 }}>{error}</p>}
            <motion.button type="submit" disabled={loading} whileHover={!loading ? { scale: 1.02 } : {}} whileTap={!loading ? { scale: 0.97 } : {}}
              style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg,#3b82f6,#7c3aed)', color: 'white', border: 'none', borderRadius: '16px', fontWeight: 800, fontSize: '15px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: 'inherit' }}>
              {loading ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} />{t('saving')}</> : t('save_password')}
            </motion.button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ─── Condor logo ──────────────────────────────────────────────────
const CondorLogo = () => (
  <motion.div
    initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
    transition={{ type: 'spring', stiffness: 160, damping: 14, delay: 0.1 }}
    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '28px' }}
  >
    <div style={{ position: 'relative' }}>
      <motion.div animate={{ scale: [1,1.15,1], opacity: [0.4,0.7,0.4] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', inset: '-8px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.35), transparent 70%)' }} />
      <motion.div animate={{ rotate: [0,5,-5,0], y: [0,-3,0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '64px' }}>
        <img src="/condor-logo.png" alt="Cóndor360" style={{ width:'200px', height:'200px', objectFit:'contain', marginBottom:'-32px' }} />
      </motion.div>
    </div>
    <div style={{ textAlign: 'center' }}>
      <h1 style={{ margin: 0, fontSize: '32px', fontWeight: 900, letterSpacing: '-1px', background: 'linear-gradient(135deg,#93c5fd 0%,#c4b5fd 50%,#f9a8d4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Condor360</h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
        style={{ margin: '4px 0 0', fontSize: '11.5px', fontWeight: 600, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.18em', textTransform: 'uppercase', fontFamily: 'monospace' }}>
        Gestión Aeronáutica
      </motion.p>
    </div>
  </motion.div>
);

// ─── BiometricSetupModal ──────────────────────────────────────────
const BiometricSetupModal = ({ onAccept, onDismiss, onNeverAsk, loading }) => {
  const { t } = useTranslation('auth');
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
        style={{ background: '#0f1929', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '24px', maxWidth: '380px', width: '100%', padding: '32px', boxShadow: '0 24px 60px rgba(0,0,0,0.5)', textAlign: 'center' }}>
        <div style={{ fontSize: '52px', marginBottom: '16px' }}>🔐</div>
        <h3 style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: 800, color: 'white' }}>{t('biometric_title')}</h3>
        <p style={{ margin: '0 0 24px', fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.65 }}
          dangerouslySetInnerHTML={{ __html: t('biometric_desc') }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <motion.button onClick={onAccept} disabled={loading} whileHover={!loading ? { scale: 1.02 } : {}} whileTap={{ scale: 0.97 }}
            style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: 'white', border: 'none', borderRadius: '16px', fontWeight: 700, fontSize: '15px', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {loading
              ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} />{t('configuring')}</>
              : t('activate_now')
            }
          </motion.button>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            <button onClick={onDismiss} style={{ width: '100%', padding: '12px', background: 'transparent', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>
              {t('not_now')}
            </button>
            <button onClick={onNeverAsk} style={{ width: '100%', padding: '9px', background: 'transparent', color: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '12px', fontWeight: 500, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
              {t('never_ask')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Main AuthScreen ──────────────────────────────────────────────
const AuthScreen = ({ onLogin, demoBootError }) => {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword]   = useState(false);
  const [formData, setFormData]           = useState({
    email:    localStorage.getItem('condor360_webauthn_email')
           || localStorage.getItem('condor360_saved_email')
           || '',
    password: '',
  });
  const [errors, setErrors]               = useState({});
  const [isLoading, setIsLoading]         = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [showForm, setShowForm]           = useState(false);
  const [rememberMe, setRememberMe]       = useState(true);
  const [showForgot, setShowForgot]       = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [showOnboarding, setShowOnboarding]         = useState(false);
  const [showRegister, setShowRegister]             = useState(false);
  const [pendingUser, setPendingUser]               = useState(null);

  const isFirstUse = !localStorage.getItem('condor360_legal_accepted');
  const [legalAccepted, setLegalAccepted] = useState(!isFirstUse);

  const {
    supported: webAuthnSupported,
    hasCredential,
    loading: biometricLoading,
    biometricLogin,
    registerBiometric,
  } = useWebAuthn();

  useEffect(() => {
    const timer = setTimeout(() => setShowForm(true), 600);
    return () => clearTimeout(timer);
  }, []);

  // Si el auto-login demo desde la landing (?demo=1) falló, mostrar el
  // cartel de error acá para que el visitante sepa qué pasó y pueda logear.
  useEffect(() => {
    if (demoBootError) setErrors(prev => ({ ...prev, general: demoBootError }));
  }, [demoBootError]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('register') === 'true') {
      setShowRegister(true);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reset_token')) return;
    const token = params.get('token');
    const error = params.get('error');
    if (token) {
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            const userWithToken = { ...data.user, token };
            localStorage.setItem('condor360_current_user', JSON.stringify(userWithToken));
            window.history.replaceState({}, '', '/');
            onLogin(userWithToken);
          } else {
            setErrors({ general: 'Error al verificar tu cuenta. Intentá de nuevo.' });
            window.history.replaceState({}, '', '/');
          }
        })
        .catch(() => {
          setErrors({ general: 'Error de conexión al verificar tu cuenta.' });
          window.history.replaceState({}, '', '/');
        });
    }
    if (error) {
      const errorMessages = {
        not_registered:     'Tu cuenta de Google no está registrada en Condor360. Contactá al administrador del aeroclub para solicitar acceso.',
        inactive:           'Tu cuenta está desactivada. Contactá al administrador del aeroclub.',
        org_inactive:       'El aeroclub está temporalmente inactivo. Contactá al administrador de Condor360.',
        no_email:           'No se pudo obtener el email de tu cuenta de Google.',
        server_error:       'Error del servidor. Intentá de nuevo más tarde.',
        google_auth_failed: 'Error al iniciar sesión con Google. Intentá de nuevo.',
      };
      setErrors({ general: errorMessages[error] || 'Error al iniciar sesión con Google.' });
      window.history.replaceState({}, '', '/');
    }
  }, []); // eslint-disable-line

  const handleInputChange = (field, value) => {
    setFormData(p => ({ ...p, [field]: value }));
    if (errors[field])  setErrors(p => ({ ...p, [field]: '' }));
    if (errors.general) setErrors(p => ({ ...p, general: '' }));
  };

  const validateForm = () => {
    const e = {};
    if (isFirstUse && !legalAccepted) e.legal = t('error_legal');
    if (!formData.email)                            e.email    = t('error_email_required');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email    = t('error_email_invalid');
    if (!formData.password)                         e.password = t('error_password_required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const persistAndLogin = (userWithToken) => {
    localStorage.setItem('condor360_legal_accepted', '1');
    localStorage.setItem('condor360_current_user', JSON.stringify(userWithToken));
    if (rememberMe) {
      localStorage.setItem('condor360_saved_email', formData.email || userWithToken.email || '');
    } else {
      localStorage.removeItem('condor360_saved_email');
    }
    sessionStorage.removeItem('condor360_current_user');
    if (webAuthnSupported && !localStorage.getItem('condor360_webauthn_registered') && !localStorage.getItem('condor360_biometric_dismissed')) {
      setPendingUser(userWithToken);
      setTimeout(() => setShowBiometricSetup(true), 900);
    } else {
      onLogin(userWithToken);
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true); setErrors({}); setLoadingStatus('');
    const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    try {
      const pingStart = Date.now();
      await Promise.race([
        fetch(`${API}/health`, { method: 'GET' }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000))
      ]);
      if (Date.now() - pingStart > 1500) {
        setLoadingStatus(t('initializing_server'));
        await new Promise(r => setTimeout(r, 1000));
      }
    } catch {
      setLoadingStatus(t('server_waking'));
      let awake = false;
      for (let i = 0; i < 15; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          await fetch(`${API}/health`, { method: 'GET' });
          awake = true;
          setLoadingStatus(t('server_ready'));
          await new Promise(r => setTimeout(r, 500));
          break;
        } catch { /* sigue esperando */ }
      }
      if (!awake) {
        setErrors({ general: t('server_unreachable') });
        setIsLoading(false); setLoadingStatus('');
        return;
      }
    }
    try {
      const response = await authService.login({ email: formData.email, password: formData.password });
      const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      if (data.success) persistAndLogin({ ...data.user, token: data.token });
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Error de conexión. Verificá el servidor.';
      setErrors({ general: msg });
    } finally { setIsLoading(false); setLoadingStatus(''); }
  };

  const handleBiometricLogin = async () => {
    if (!formData.email) { setErrors({ email: t('error_email_required') }); return; }
    setErrors({});
    try {
      const { user, token } = await biometricLogin(formData.email);
      persistAndLogin({ ...user, token });
    } catch (err) { setErrors({ general: err.message }); }
  };

  const [isDemoLoading, setIsDemoLoading] = useState(false);

  const handleDemoLogin = async () => {
    if (shouldRedirectToDemoHost(window.location.hostname)) {
      // Red de seguridad: la demo se separó a su propia infra. En prod,
      // "Probar demo" ya no hace login local (que va a desaparecer junto
      // con la demo de la base de prod) — manda al dominio de la demo.
      window.location.href = DEMO_HOST_REDIRECT_TARGET;
      return;
    }
    setErrors({}); setIsDemoLoading(true);
    try {
      const response = await authService.demoLogin('admin');
      const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      if (data.success) {
        localStorage.setItem('condor360_legal_accepted', '1');
        // Persistir access + refresh vía apiService para que el auto-refresh
        // funcione durante la demo (el JWT dura 15 min).
        storeTokens(data.token, data.refreshToken, data.user);
        onLogin({ ...data.user, token: data.token, expiresIn: data.expiresIn });
      }
    } catch (error) {
      const status = error.response?.status;
      const msg = status === 429
        ? t('demo_rate_limited')
        : status === 404
          ? t('demo_unavailable')
          : (error.response?.data?.message || t('demo_unavailable'));
      setErrors({ general: msg });
    } finally { setIsDemoLoading(false); }
  };

  const handleBiometricSetup = async () => {
    try {
      const stored = localStorage.getItem('condor360_current_user') || sessionStorage.getItem('condor360_current_user');
      const u = stored ? JSON.parse(stored) : null;
      if (!u?.token) { setShowBiometricSetup(false); onLogin(pendingUser); return; }
      localStorage.setItem('condor360_webauthn_email', formData.email || u.email || '');
      await registerBiometric(u.token, 'Mi dispositivo');
    } catch { /* igual dejamos entrar */ }
    finally { setShowBiometricSetup(false); onLogin(pendingUser); }
  };

  const resetToken = new URLSearchParams(window.location.search).get('reset_token');
  if (showRegister) return <RegisterAeroclub onBack={() => setShowRegister(false)} />;
  if (resetToken) return <ResetPasswordScreen token={resetToken} onDone={() => window.history.replaceState({}, '', '/')} />;

  const linkStyle = {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '11.5px', fontWeight: 500,
    textDecoration: 'underline', padding: 0,
    color: 'rgba(255,255,255,0.28)',
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', overflow: 'hidden' }}>
      <HUDBackground />

      <motion.button
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        onClick={() => setShowOnboarding(true)}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        style={{
          position: 'fixed', top: '16px', right: '16px', zIndex: 100,
          display: 'flex', alignItems: 'center', gap: '7px',
          padding: '9px 14px', borderRadius: '14px', cursor: 'pointer',
          background: 'rgba(99,102,241,0.18)', backdropFilter: 'blur(12px)',
          border: '1px solid rgba(99,102,241,0.3)',
          color: 'rgba(165,180,252,0.9)',
          fontSize: '13px', fontWeight: 700, fontFamily: 'inherit',
          boxShadow: '0 4px 16px rgba(99,102,241,0.15)',
        }}
      >
        <span style={{ fontSize: '15px', lineHeight: 1 }}>?</span>
        {t('help')}
      </motion.button>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94 }}
            transition={{ type: 'spring', stiffness: 130, damping: 18 }}
            style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '420px' }}
          >
            <div style={{
              borderRadius: '28px', overflow: 'hidden',
              background: 'rgba(10,15,30,0.92)', // ✅ más opaco como fallback iOS
              backdropFilter: 'blur(28px)',
              WebkitBackdropFilter: 'blur(28px)', // ✅ requerido para Safari iOS
              border: '1px solid rgba(99,102,241,0.2)',
              isolation: 'isolate', // ✅ stacking context para iOS
              boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.06)',
              padding: '36px 32px 32px',
            }}>
              <CondorLogo />

              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', borderRadius: '14px', background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.18)', marginBottom: '24px' }}>
                <span style={{ fontSize: '18px', marginTop: '1px' }}>✈️</span>
                <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: t('access_notice') }} />
              </motion.div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.55)', marginBottom: '7px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('email_label')}</label>
                  <AuthInput icon={Mail} type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} placeholder="tu@email.com" error={errors.email} /> 
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,0.55)', marginBottom: '7px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('password_label')}</label>
                  <AuthInput icon={Lock} type={showPassword ? 'text' : 'password'} value={formData.password}
                    onChange={e => handleInputChange('password', e.target.value)} placeholder="••••••••" error={errors.password}
                    rightEl={
                      <button type="button" onClick={() => setShowPassword(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center' }}>
                        {showPassword ? <EyeOff style={{ width: '16px', height: '16px' }} /> : <Eye style={{ width: '16px', height: '16px' }} />}
                      </button>
                    }
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <div onClick={() => setRememberMe(p => !p)}
                      style={{ width: '18px', height: '18px', borderRadius: '5px', cursor: 'pointer', flexShrink: 0, background: rememberMe ? 'linear-gradient(135deg,#4f46e5,#7c3aed)' : 'rgba(255,255,255,0.07)', border: `1.5px solid ${rememberMe ? '#7c3aed' : 'rgba(255,255,255,0.15)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.18s' }}>
                      {rememberMe && <span style={{ color: 'white', fontSize: '11px', fontWeight: 800, lineHeight: 1 }}>✓</span>}
                    </div>
                    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', userSelect: 'none' }}>{t('remember_me')}</span>
                  </label>
                  <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: 'rgba(147,197,253,0.8)', padding: 0 }} onClick={() => setShowForgot(true)}>
                    {t('forgot_password')}
                  </button>
                </div>

                {/* ✅ Checkbox legal — solo primer uso */}
                {isFirstUse && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                    style={{ padding: '12px 14px', borderRadius: '14px', background: 'rgba(99,102,241,0.07)', border: `1.5px solid ${legalAccepted ? 'rgba(99,102,241,0.35)' : errors.legal ? 'rgba(248,113,113,0.4)' : 'rgba(99,102,241,0.2)'}`, transition: 'border-color 0.2s' }}
                  >
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                      <div
                        onClick={() => { setLegalAccepted(p => !p); if (errors.legal) setErrors(p => ({ ...p, legal: '' })); }}
                        style={{ width: '18px', height: '18px', borderRadius: '5px', cursor: 'pointer', flexShrink: 0, marginTop: '1px', background: legalAccepted ? 'linear-gradient(135deg,#4f46e5,#7c3aed)' : 'rgba(255,255,255,0.07)', border: `1.5px solid ${legalAccepted ? '#7c3aed' : errors.legal ? 'rgba(248,113,113,0.5)' : 'rgba(255,255,255,0.15)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.18s' }}
                      >
                        {legalAccepted && <span style={{ color: 'white', fontSize: '11px', fontWeight: 800, lineHeight: 1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, userSelect: 'none' }}>
                        <span dangerouslySetInnerHTML={{ __html: t('legal_checkbox') }} />{' '}
                        <a href="/docs/Condor360_TyC_v1.4.pdf" target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: 'rgba(147,197,253,0.85)', fontWeight: 600, textDecoration: 'underline' }}>{t('terms')}</a>
                        {' '}{t('legal_checkbox_and')}{' '}
                        <a href="/docs/Condor360_Politica_Privacidad_v1.4.pdf" target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: 'rgba(147,197,253,0.85)', fontWeight: 600, textDecoration: 'underline' }}>{t('privacy')}</a>.
                      </span>
                    </label>
                    {errors.legal && (
                      <p style={{ margin: '6px 0 0 28px', fontSize: '12px', color: 'rgba(248,113,113,0.9)', fontWeight: 500 }}>{errors.legal}</p>
                    )}
                  </motion.div>
                )}

                <AnimatePresence>
                  {errors.general && (
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      style={{ padding: '12px 14px', borderRadius: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <AlertCircle style={{ width: '16px', height: '16px', color: 'rgba(248,113,113,0.9)', flexShrink: 0, marginTop: '1px' }} />
                      <p style={{ margin: 0, fontSize: '13px', color: 'rgba(248,113,113,0.9)', lineHeight: 1.5 }}>{errors.general}</p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '2px 0' }}>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', fontWeight: 600, letterSpacing: '0.08em' }}>O</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
                </div>

                {webAuthnSupported && hasCredential && (
                  <motion.button type="button" onClick={handleBiometricLogin} disabled={biometricLoading}
                    whileHover={{ scale: 1.02, y: -1 }} whileTap={{ scale: 0.97 }}
                    style={{ width: '100%', padding: '13px', border: '1.5px solid rgba(99,102,241,0.3)', borderRadius: '16px', cursor: biometricLoading ? 'not-allowed' : 'pointer', background: 'rgba(99,102,241,0.08)', color: 'white', fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontFamily: 'inherit' }}>
                    {biometricLoading
                      ? <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '17px', height: '17px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'rgba(147,197,253,0.8)' }} />
                      : <span style={{ fontSize: '19px' }}>🔐</span>
                    }
                    {biometricLoading ? t('verifying') : t('biometric_login')}
                  </motion.button>
                )}

                <motion.button type="button"
                  onClick={() => { const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000'; window.location.href = `${apiUrl}/auth/google`; }}
                  whileHover={{ scale: 1.02, y: -1 }} whileTap={{ scale: 0.97 }}
                  style={{ width: '100%', padding: '13px', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '16px', cursor: 'pointer', background: 'rgba(255,255,255,0.06)', color: 'white', fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', fontFamily: 'inherit', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                    <path d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                  {t('google_login')}
                </motion.button>

                <motion.button type="submit" disabled={isLoading}
                  whileHover={!isLoading ? { scale: 1.02, y: -1 } : {}} whileTap={!isLoading ? { scale: 0.97 } : {}}
                  style={{ position: 'relative', width: '100%', padding: '15px', border: 'none', borderRadius: '16px', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: '16px', color: 'white', overflow: 'hidden', fontFamily: 'inherit', boxShadow: '0 8px 28px rgba(79,70,229,0.45)' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#4f46e5 0%,#7c3aed 60%,#9333ea 100%)' }} />
                  <motion.div animate={{ x: ['-100%','200%'] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'linear', repeatDelay: 1 }}
                    style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent)', pointerEvents: 'none' }} />
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px' }}>
                    {isLoading
                      ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} />{loadingStatus || t('processing')}</>
                      : <><LogIn style={{ width: '18px', height: '18px' }} />{t('sign_in')}</>
                    }
                  </div>
                </motion.button>

                <motion.button type="button" onClick={handleDemoLogin} disabled={isDemoLoading || isLoading}
                  whileHover={!isDemoLoading && !isLoading ? { scale: 1.02, y: -1 } : {}} whileTap={!isDemoLoading && !isLoading ? { scale: 0.97 } : {}}
                  style={{ width: '100%', padding: '13px', border: '1.5px solid rgba(147,197,253,0.35)', borderRadius: '16px', cursor: (isDemoLoading || isLoading) ? 'not-allowed' : 'pointer', background: 'rgba(59,130,246,0.09)', color: 'rgba(191,219,254,0.95)', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px', fontFamily: 'inherit' }}>
                  {isDemoLoading
                    ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ width: '15px', height: '15px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'rgba(191,219,254,0.95)' }} />{t('try_demo_starting')}</>
                    : <><span style={{ fontSize: '17px', lineHeight: 1 }}>🎬</span>{t('try_demo')}</>
                  }
                </motion.button>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', paddingTop: '4px' }}>
                  <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>{t('register_cta')}</span>
                  <motion.button type="button" onClick={() => setShowRegister(true)}
                    whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '13px', fontWeight: 700, color: 'rgba(165,180,252,0.9)', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                    {t('register_link')}
                  </motion.button>
                </motion.div>
              </form>
            </div>

            {/* ── Footer: links legales + selector de idioma ── */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px' }}>
              <a href="/docs/Condor360_TyC_v1.4.pdf" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                {t('terms')}
              </a>
              <span style={{ color: 'rgba(255,255,255,0.12)', fontSize: '14px' }}>·</span>
              <a href="/docs/Condor360_Politica_Privacidad_v1.4.pdf" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                {t('privacy')}
              </a>
              <span style={{ color: 'rgba(255,255,255,0.12)', fontSize: '14px' }}>·</span>
              <LanguageSwitcher />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        input::placeholder { color: rgba(255,255,255,0.25) !important; }
        input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px rgba(10,15,30,0.95) inset !important; -webkit-text-fill-color: white !important; }
      `}</style>

      <AnimatePresence>
        {showForgot  && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
        {showBiometricSetup && (
          <BiometricSetupModal
            onAccept={handleBiometricSetup}
            onDismiss={() => { setShowBiometricSetup(false); onLogin(pendingUser); }}
            onNeverAsk={() => { localStorage.setItem('condor360_biometric_dismissed', '1'); setShowBiometricSetup(false); onLogin(pendingUser); }}
            loading={biometricLoading}
          />
        )}
        {showOnboarding && <OnboardingSlides onComplete={() => setShowOnboarding(false)} darkMode={false} />}
      </AnimatePresence>
    </div>
  );
};

export default AuthScreen;
